import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';

const VariantManager = ({ product, onUpdate }) => {
  const { token } = useAuth();
  const [variants, setVariants] = useState([]);
  const [allVariants, setAllVariants] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVariants();
  }, [product]);

  const fetchVariants = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/${product.id}/variants`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        setVariants(data);
        generateAllVariants(data);
      }
    } catch (error) {
      toast.error('Failed to load variants');
    } finally {
      setLoading(false);
    }
  };

  const generateAllVariants = (existingVariants) => {
    const options = [];
    
    if (product.has_ohms && product.ohms_options && product.ohms_options.length > 0) {
      options.push({ type: 'ohms', values: product.ohms_options });
    }
    if (product.has_flavour && product.flavour_options && product.flavour_options.length > 0) {
      options.push({ type: 'flavour', values: product.flavour_options });
    }
    if (product.has_colour && product.colour_options && product.colour_options.length > 0) {
      options.push({ type: 'colour', values: product.colour_options });
    }
    if (product.has_strength && product.strength_options && product.strength_options.length > 0) {
      options.push({ type: 'strength', values: product.strength_options });
    }

    if (options.length === 0) {
      setAllVariants([]);
      return;
    }

    const combinations = [];
    
    function generate(current, remaining) {
      if (remaining.length === 0) {
        combinations.push(current);
        return;
      }
      
      const next = remaining[0];
      const rest = remaining.slice(1);
      
      for (let i = 0; i < next.values.length; i++) {
        const newCurrent = {};
        for (const key in current) {
          newCurrent[key] = current[key];
        }
        newCurrent[next.type] = next.values[i];
        generate(newCurrent, rest);
      }
    }
    
    generate({}, options);
    
    const result = [];
    for (let i = 0; i < combinations.length; i++) {
      const combo = combinations[i];
      let existing = null;
      
      for (let j = 0; j < existingVariants.length; j++) {
        const v = existingVariants[j];
        const ohmMatch = !combo.ohms || v.ohms === combo.ohms;
        const flavourMatch = !combo.flavour || v.flavour === combo.flavour;
        const colourMatch = !combo.colour || v.colour === combo.colour;
        const strengthMatch = !combo.strength || v.strength === combo.strength;
        
        if (ohmMatch && flavourMatch && colourMatch && strengthMatch) {
          existing = v;
          break;
        }
      }
      
      result.push({
        ohms: combo.ohms || null,
        flavour: combo.flavour || null,
        colour: combo.colour || null,
        strength: combo.strength || null,
        quantity: existing ? existing.quantity : 0,
        id: existing ? existing.id : null
      });
    }
    
    setAllVariants(result);
  };

  const updateVariantQuantity = async (variant, newQuantity) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/products/${product.id}/variants`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ohms: variant.ohms,
            flavour: variant.flavour,
            colour: variant.colour,
            strength: variant.strength,
            quantity: parseInt(newQuantity) || 0
          })
        }
      );

      if (response.ok) {
        toast.success('Quantity updated');
        fetchVariants();
        onUpdate();
      }
    } catch (error) {
      toast.error('Failed to update quantity');
    }
  };

  if (loading) {
    return <div className="text-center p-8 text-muted-foreground font-mono">Loading variants...</div>;
  }

  if (allVariants.length === 0) {
    return <div className="text-center p-8 text-muted-foreground">No variant options configured. Edit the product to add variant types.</div>;
  }

  return (
    <div className="space-y-2">
      {allVariants.map((variant, idx) => {
        const labelParts = [];
        if (variant.ohms) labelParts.push(`${variant.ohms}Ω`);
        if (variant.flavour) labelParts.push(variant.flavour);
        if (variant.colour) labelParts.push(variant.colour);
        if (variant.strength) labelParts.push(variant.strength);
        const variantLabel = labelParts.join(' / ');

        return (
          <div key={idx} className="flex items-center gap-4 p-4 bg-secondary rounded-md border border-border">
            <div className="flex-1 font-body font-medium">{variantLabel}</div>
            <div className="flex items-center gap-2">
              <Label className="font-mono text-xs">Quantity:</Label>
              <Input
                type="number"
                min="0"
                value={variant.quantity}
                onChange={(e) => updateVariantQuantity(variant, e.target.value)}
                className="w-24"
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default VariantManager;